import React, { useEffect, useState } from "react";
import { useStores } from "../../../use-stores";
import { observer } from "mobx-react";
import { Box, Button, useTheme, Grid2 } from "@mui/material";
import CustomTypography from "../../components/common/form/CustomTypography";
import { DateRangePickerNew } from "../../components/common/textFields/DatePickerRangeNew";
import { Dayjs } from "dayjs";
import { useSearchParams } from "react-router-dom";

import AppDropDown from "../../components/common/filter/AppDropDown";
import AppButton from "../../components/common/buttons/AppButton";
import { useNotification } from "../../../utils/NotificationsContext";
import LoaderOverlay from "../../components/common/pages/AppLoader";
import { Admin } from "../../../models/TaskTracker/Admin";
import AppSearchDropDown2 from "../../components/common/filter/AppSearchDropdown2";
import PermissionBlockLabel from "../../components/common/pop-up/PermissionBlockLabel ";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import { SortType } from "../../../models/SortType";
import { FilterValue } from "../../../models/Filters/FilterValue";
import {
  parseQueryParamsForQCAnalyticsFilters,
  QCAnalyticsFilters,
  QCAnalyticsSelectedFilters,
} from "../../../models/Dashboard/QCDashboard/QCAnalyticsFilters";
import TypeFilterCard from "../../components/filters/TypeFilterCard";
import { Product } from "../../../models/Products/Product";

const QC_ANALYTICS_FILTERS_KEY = "QC_ANALYTICS_FILTERS_KEY";

const QC_TIME_OPTIONS: FilterValue[] = [
  { id: "0", value: "Select" },
  { id: "3600", value: "1 hour" },
  { id: "18000", value: "6 hours" },
  { id: "43200", value: "12 hours" },
  { id: "86400", value: "24 hours" },
  { id: "172800", value: "48 hours" },
];

const QCAnalyticsDashboard: React.FC = () => {
  const theme = useTheme();

  const {
    qcAnalyticsDashboardStore,
    projectStore,
    orderStore,
    TaskTrackerStore,
    userStore,
    productStore,
  } = useStores();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedFiltersData, setSelectedFiltersData] = useState<FilterValue[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const { showError } = useNotification();
  let apiTimer: any;

  const initialFilters: QCAnalyticsFilters =
    parseQueryParamsForQCAnalyticsFilters(
      localStorage.getItem(QC_ANALYTICS_FILTERS_KEY)
        ? JSON.parse(localStorage.getItem(QC_ANALYTICS_FILTERS_KEY)!)
        : null,
      searchParams
    );

  const [filters, setFilters] = useState<QCAnalyticsFilters>(initialFilters);
  const [selectedFilters, setSelectedFilters] =
    useState<QCAnalyticsSelectedFilters>({
      ...initialFilters,
      start_date: initialFilters.start_date.format("YYYY-MM-DD"),
      end_date: initialFilters.end_date.format("YYYY-MM-DD"),
    });

  useEffect(() => {
    updateSelectedFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    handleApply();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const newSearchParams = new URLSearchParams();
    Object.entries(selectedFilters).forEach(([key, value]) => {
      if (value) {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            newSearchParams.set(key, value.map((item) => item.id).join(","));
          }
        } else {
          if (key === "qc_time") {
            newSearchParams.set(key, value.id);
          } else {
            newSearchParams.set(key, value);
          }
        }
      }
    });

    setSearchParams(newSearchParams);
  }, [selectedFilters]);

  const updateSelectedFilters = () => {
    localStorage.setItem(QC_ANALYTICS_FILTERS_KEY, JSON.stringify(filters));

    const start_date = filters.start_date.format("YYYY-MM-DD");
    const end_date = filters.end_date.format("YYYY-MM-DD");

    setSelectedFilters((_) => ({
      ...filters,
      start_date: start_date,
      end_date: end_date,
    }));

    const newFilters: React.SetStateAction<FilterValue[]> = [];

    if (filters.admin_id) {
      filters.admin_id.forEach((admin_id) =>
        newFilters.push({ id: "admin_id", value: admin_id.value })
      );
    }

    if (filters.order_id) {
      filters.order_id.forEach((order_id) =>
        newFilters.push({ id: "order_id", value: order_id.value })
      );
    }
    if (filters.order_type) {
      filters.order_type.forEach((order_type) =>
        newFilters.push({ id: "order_type", value: order_type.value })
      );
    }

    if (filters.product_id) {
      filters.product_id.forEach((product_id) =>
        newFilters.push({ id: "product_id", value: product_id.value })
      );
    }

    if (filters.qc_time) {
      newFilters.push({
        id: "qc_time",
        value: filters.qc_time.id,
      });
    }

    setSelectedFiltersData(newFilters);
  };

  useEffect(() => {
    projectStore.getTypeOfOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApply = async () => {
    setLoading(true);

    try {
      setFilters((prev) => ({
        ...prev,
      }));

      await qcAnalyticsDashboardStore.getQCAnalyticsTats({
        admin_id: selectedFilters.admin_id,
        order_id: selectedFilters.order_id,
        order_type: selectedFilters.order_type,
        start_date: selectedFilters.start_date,
        end_date: selectedFilters.end_date,
        product_id: selectedFilters.product_id,
        excluded_orders:
          selectedFilters.excluded_orders?.id === "true" ? true : false,
        qc_time: selectedFilters.qc_time?.id || null,
      });

      await qcAnalyticsDashboardStore.getQCAnalyticsPrintTats({
        admin_id: selectedFilters.admin_id,
        order_id: selectedFilters.order_id,
        order_type: selectedFilters.order_type,
        start_date: selectedFilters.start_date,
        end_date: selectedFilters.end_date,
        product_id: selectedFilters.product_id,
        excluded_orders:
          selectedFilters.excluded_orders?.id === "true" ? true : false,
        qc_time: selectedFilters.qc_time?.id || null,
      });
    } catch (error) {
      showError("Error", "Failed to fetch the analytics dashboard details 2.");
    } finally {
      setTimeout(() => setLoading(false), 500);
    }
  };

  const handleRangeSelect = (range: {
    startDate: Dayjs | null;
    endDate: Dayjs | null;
  }) => {
    if (range.startDate && range.endDate) {
      setFilters((prev) => ({
        ...prev,
        start_date: range.startDate!,
        end_date: range.endDate!,
      }));
    }
  };

  const handleRemoveFilter = (index: number) => {
    const filterToRemove = selectedFiltersData[index];
    const updatedFilters = selectedFiltersData.filter((_, i) => i !== index);
    setSelectedFiltersData(updatedFilters);

    switch (filterToRemove.id) {
      case "admin_id":
        setFilters((prev) => ({
          ...prev,
          admin_id:
            filters.admin_id?.filter(
              (item) => item.value !== filterToRemove.value
            ) || [],
        }));
        break;

      case "order_type":
        setFilters((prev) => ({
          ...prev,
          order_type:
            prev.order_type?.filter(
              (item) => item.value !== filterToRemove.value
            ) || [],
        }));
        break;

      case "order_id":
        setFilters((prev) => ({
          ...prev,
          order_id:
            prev.order_id?.filter(
              (item) => item.value !== filterToRemove.value
            ) || [],
        }));
        break;

      case "product_id":
        setFilters((prev) => ({
          ...prev,
          product_id:
            prev.product_id?.filter(
              (item) => item.value !== filterToRemove.value
            ) || [],
        }));
        break;

      case "qc_time":
        setFilters((prev) => ({
          ...prev,
          qc_time:
            QC_TIME_OPTIONS.find((item) => item.id === filterToRemove.id) ||
            null,
        }));
        break;

      case "excluded_orders":
        setFilters((prev) => ({
          ...prev,
          excluded_orders: null,
        }));
        break;
      default:
        break;
    }
  };

  const handleClearAllFilters = () => {
    setFilters((_) => ({
      ...initialFilters,
      pm_adminids: [],
      order_id: [],
      order_type: [],
      archived: [],
    }));

    setSelectedFiltersData([]);
  };

  const handleOrderIdSearch = (searchText?: string) => {
    if (apiTimer) clearTimeout(apiTimer);
    if (searchText && searchText.length > 1)
      apiTimer = setTimeout(() => {
        orderStore.searchOrderIds(searchText).catch((err) => {});
      }, 1500);
  };

  const handleAdminSearch = (searchText: string) => {
    if (apiTimer) clearTimeout(apiTimer);

    if (searchText && searchText.length > 2)
      apiTimer = setTimeout(() => {
        TaskTrackerStore.getAdminBySearch(searchText).catch((err) => {
          showError("Error", err?.response?.data?.message);
        });
      }, 1000);
  };

  const handleProductSearch = (searchText: string) => {
    if (apiTimer) clearTimeout(apiTimer);

    if (searchText && searchText.length > 2)
      apiTimer = setTimeout(() => {
        productStore
          .getProducts({
            limit: 30,
            page_no: 1,
            name: searchText,
            sortBy: "name",
            sortType: SortType.ASC,
          })
          .catch((err) => {
            console.log(err);
          });
      }, 1000);
  };

  return (
    <>
      {userStore.hasPermission(EmployeeRoles.VIEW_QC_DASHBOARD) ? (
        <>
          <LoaderOverlay loading={loading} />
          <Box sx={{ margin: "8px" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                backgroundColor: theme.palette.customColors.white,
                padding: 4,
                borderRadius: 2,
                gap: 2,
              }}
            >
              <Grid2 container spacing={2} columns={12} sx={{ width: "100%" }}>
                <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}>
                  <CustomTypography
                    variant="heading8"
                    color={theme.palette.customColors.text}
                    style={{ marginBottom: 6 }}
                  >
                    Date Range
                  </CustomTypography>
                  <DateRangePickerNew
                    onRangeSelect={handleRangeSelect}
                    startDate={filters.start_date}
                    endDate={filters.end_date}
                  />
                </Grid2>
                <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}>
                  <CustomTypography
                    variant="heading8"
                    color={theme.palette.customColors.text}
                    style={{ marginBottom: 6 }}
                  >
                    {"POC"}
                  </CustomTypography>
                  <AppSearchDropDown2
                    showSelection={false}
                    isMultiSelect={true}
                    title="Select Poc"
                    fontSize="0.9rem"
                    value={filters.admin_id || []}
                    list={
                      TaskTrackerStore?.admins?.map((admin: Admin) => ({
                        id: admin.id.toString(),
                        value: admin.name,
                      })) || []
                    }
                    local={false}
                    handleSearch={(admin_id) => handleAdminSearch(admin_id!)}
                    handleSelect={(items) => {
                      setFilters((prev) => ({
                        ...prev,
                        admin_id: items,
                      }));
                    }}
                  />
                </Grid2>
                <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}>
                  <CustomTypography
                    variant="heading8"
                    color={theme.palette.customColors.text}
                    style={{ marginBottom: 6 }}
                  >
                    {"Order Type"}
                  </CustomTypography>
                  <AppDropDown
                    title=""
                    isMultiSelect={true}
                    valuesList={
                      filters.order_type?.map((item) => item.id) || []
                    }
                    list={
                      projectStore?.orderType?.map((order) => ({
                        id: order.id.toString(),
                        value: order.value,
                      })) || []
                    }
                    fontSize={"12px"}
                    onMultiSelectionChange={(ids) => {
                      setFilters((prev) => ({
                        ...prev,
                        order_type:
                          projectStore?.orderType?.filter((item) =>
                            ids.includes(item.id)
                          ) || [],
                      }));
                    }}
                  />
                </Grid2>
                <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}>
                  <CustomTypography
                    variant="heading8"
                    color={theme.palette.customColors.text}
                    style={{ marginBottom: 6 }}
                  >
                    {"Product ID"}
                  </CustomTypography>
                  <AppSearchDropDown2
                    showSelection={false}
                    isMultiSelect={true}
                    title="Select Product"
                    fontSize="0.9rem"
                    value={filters.product_id || []}
                    list={
                      productStore?.products?.map((product: Product) => ({
                        id: product.id?.toString() || "",
                        value: product.name || "",
                      })) || []
                    }
                    local={false}
                    handleSearch={(product_id) =>
                      handleProductSearch(product_id!)
                    }
                    handleSelect={(items) => {
                      setFilters((prev) => ({
                        ...prev,
                        product_id: items,
                      }));
                    }}
                  />
                </Grid2>
              </Grid2>
              <Grid2 container spacing={2} columns={12} sx={{ width: "100%" }}>
                <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}>
                  <CustomTypography
                    variant="heading8"
                    color={theme.palette.customColors.text}
                    style={{ marginBottom: 6 }}
                  >
                    Order ID
                  </CustomTypography>
                  <AppSearchDropDown2
                    showSelection={false}
                    isMultiSelect={true}
                    title="Order Id"
                    value={filters.order_id || []}
                    fontSize="12px"
                    list={orderStore.orderIds.map((item) => {
                      return { id: String(item), value: String(item) };
                    })}
                    local={false}
                    handleSearch={handleOrderIdSearch}
                    handleSelect={(items) => {
                      setFilters((prev) => ({
                        ...prev,
                        order_id: items,
                      }));
                    }}
                  />
                </Grid2>
                <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}>
                  <CustomTypography
                    variant="heading8"
                    color={theme.palette.customColors.text}
                    style={{ marginBottom: 6 }}
                  >
                    QC Time
                  </CustomTypography>
                  <AppDropDown
                    title=""
                    isMultiSelect={false}
                    value={filters.qc_time?.id || "0"}
                    list={
                      QC_TIME_OPTIONS.map((qc_time) => ({
                        id: qc_time.id,
                        value: qc_time.value,
                      })) || []
                    }
                    fontSize={"12px"}
                    onSingleSelectionChange={(id) => {
                      setFilters((prev) => ({
                        ...prev,
                        qc_time:
                          QC_TIME_OPTIONS.find((item) => item.id === id) ||
                          null,
                      }));
                    }}
                  />
                </Grid2>
                <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}></Grid2>
                <Grid2
                  size={{ lg: 3, md: 4, sm: 12, xs: 12 }}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-end",
                    gap: 2,
                  }}
                >
                  <AppButton
                    variant="primaryBlock"
                    label="Search"
                    onClick={handleApply}
                    customStyles={{ width: "100%" }}
                    disabled={loading}
                  />
                  <AppButton
                    variant="transparentPrimary"
                    label="Clear Filters"
                    customStyles={{ width: "100%" }}
                    onClick={() => handleClearAllFilters()}
                  />
                </Grid2>
              </Grid2>

              <Grid2 container spacing={2} columns={12} sx={{ width: "100%" }}>
                {selectedFiltersData.length > 0 && (
                  <Box sx={{ paddingTop: "15px" }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 2,
                        borderRadius: 2,
                      }}
                    >
                      {selectedFiltersData.map((filter, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            backgroundColor:
                              theme.palette.customColors.lightPurple,
                            borderRadius: "8px",
                            border: "1px solid",
                            borderColor: theme.palette.customColors.blackBorder,
                            height: "34px",
                            paddingX: "6px",
                          }}
                        >
                          <CustomTypography
                            variant="heading9"
                            color={theme.palette.customColors.text}
                            style={{ marginRight: "5px" }}
                          >
                            {filter.value}
                          </CustomTypography>
                          <Button
                            onClick={() => handleRemoveFilter(index)}
                            sx={{
                              width: "14px",
                              height: "14px",
                              minWidth: "14px",
                              minHeight: "14px",
                              fontSize: "15px",
                              padding: "0px",
                              border: "1px solid #E2E2E2",
                              borderRadius: "50%",
                              backgroundColor: theme.palette.customColors.white,
                              cursor: "pointer",
                              overflow: "hidden",
                              "&:hover": {
                                backgroundColor:
                                  theme.palette.customColors.lightGreyColor,
                              },
                            }}
                          >
                            ×
                          </Button>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Grid2>
            </Box>
            <Box sx={{ marginTop: 1, marginBottom: 3 }}>
              <CustomTypography
                variant="heading8"
                color={theme.palette.customColors.blackBorder}
              >
                <span style={{ fontStyle: "italic" }}>Note:</span> The analytics
                data is cached. Recent changes may take up to 30 minutes to
                reflect. QC data is captured post 2nd June, 2025.
              </CustomTypography>
            </Box>
            <Box sx={{ gap: 2, display: "flex", flexDirection: "column" }}>
              {qcAnalyticsDashboardStore.basic_tats?.length > 0 && (
                <Box sx={{ gap: 2, display: "flex", flexDirection: "row" }}>
                  <hr style={{ height: 1, width: "100%" }} />
                  <CustomTypography
                    variant="heading7"
                    color={theme.palette.customColors.blackBorder}
                    style={{
                      minWidth: "90px",
                      textAlign: "center",
                    }}
                  >
                    Raw Inward
                  </CustomTypography>
                  <hr style={{ height: 1, width: "100%" }} />
                </Box>
              )}
              <Grid2 container spacing={2} columns={12} sx={{ width: "100%" }}>
                {qcAnalyticsDashboardStore.basic_tats?.map((item, index) => (
                  <Grid2
                    size={{ lg: 2, md: 2, sm: 12, xs: 12 }}
                    key={`${item.label}-${index}`}
                  >
                    <TypeFilterCard
                      title={item.label}
                      message={item.info}
                      value={item.value ?? "0"}
                      color={""}
                      icon={
                        "https://content.swageazy.com/icons/boxes_green.png"
                      }
                      onClick={(type) => {
                        let url = "";
                        switch (type) {
                          case "Total Inwards":
                            url = `/report/data?title=${encodeURI(
                              "Total Inwards"
                            )}&base_url=/v5/ops-analytics/qc&path=/data/qc-time&${searchParams.toString()}`;
                            window.open(url, "_blank");
                            break;
                          case "Avg Time (mins)":
                            url = `/report/data?title=${encodeURI(
                              "Avg Time (mins)"
                            )}&base_url=/v5/ops-analytics/qc&path=/data/qc-time&${searchParams.toString()}`;
                            window.open(url, "_blank");
                            break;
                          case "Inward Qty of Products":
                            url = `/report/data?title=${encodeURI(
                              "Inward Qty of Products"
                            )}&base_url=/v5/ops-analytics/qc&path=/data/qc-time&${searchParams.toString()}`;
                            window.open(url, "_blank");
                            break;
                          case "Rejected Qty of Products":
                            url = `/report/data?title=${encodeURI(
                              "Rejected Qty of Products"
                            )}&base_url=/v5/ops-analytics/qc&path=/data/qc-time&${searchParams.toString()}`;
                            window.open(url, "_blank");
                            break;
                          case "QC Pending":
                            url = `/report/data?title=${encodeURI(
                              "QC Pending"
                            )}&base_url=/v5/ops-analytics/qc&path=/data/qc-pending&${searchParams.toString()}`;
                            window.open(url, "_blank");
                            break;
                          case "QC In Progress":
                            url = `/report/data?title=${encodeURI(
                              "QC In Progress"
                            )}&base_url=/v5/ops-analytics/qc&path=/data/qc-in-progress&${searchParams.toString()}`;
                            window.open(url, "_blank");
                            break;
                          case "QC Completed":
                            url = `/report/data?title=${encodeURI(
                              "QC Completed"
                            )}&base_url=/v5/ops-analytics/qc&path=/data/qc-completed&${searchParams.toString()}`;
                            window.open(url, "_blank");
                            break;
                        }
                      }}
                    />
                  </Grid2>
                ))}
              </Grid2>
              {qcAnalyticsDashboardStore.print_basic_tats?.length > 0 && (
                <Box sx={{ gap: 2, display: "flex", flexDirection: "row" }}>
                  <hr style={{ height: 1, width: "100%" }} />
                  <CustomTypography
                    variant="heading7"
                    color={theme.palette.customColors.blackBorder}
                    style={{
                      minWidth: "90px",
                      textAlign: "center",
                    }}
                  >
                    Print Inward
                  </CustomTypography>
                  <hr style={{ height: 1, width: "100%" }} />
                </Box>
              )}
              <Grid2 container spacing={2} columns={12} sx={{ width: "100%" }}>
                {qcAnalyticsDashboardStore.print_basic_tats?.map(
                  (item, index) => (
                    <Grid2
                      size={{ lg: 2, md: 2, sm: 12, xs: 12 }}
                      key={`${item.label}-${index}`}
                    >
                      <TypeFilterCard
                        title={item.label}
                        message={item.info}
                        value={item.value ?? "0"}
                        color={""}
                        icon={
                          "https://content.swageazy.com/icons/boxes_green.png"
                        }
                        onClick={(type) => {
                          let url = "";
                          switch (type) {
                            case "Total Inwards":
                              url = `/report/data?title=${encodeURI(
                                "Total Inwards"
                              )}&base_url=/v5/ops-analytics/qc&path=/data/print-qc-time&${searchParams.toString()}`;
                              window.open(url, "_blank");
                              break;
                            case "Avg Time (mins)":
                              url = `/report/data?title=${encodeURI(
                                "Avg Time (mins)"
                              )}&base_url=/v5/ops-analytics/qc&path=/data/print-qc-time&${searchParams.toString()}`;
                              window.open(url, "_blank");
                              break;
                            case "Inward Qty of Products":
                              url = `/report/data?title=${encodeURI(
                                "Inward Qty of Products"
                              )}&base_url=/v5/ops-analytics/qc&path=/data/print-qc-time&${searchParams.toString()}`;
                              window.open(url, "_blank");
                              break;
                            case "Rejected Qty of Products":
                              url = `/report/data?title=${encodeURI(
                                "Rejected Qty of Products"
                              )}&base_url=/v5/ops-analytics/qc&path=/data/print-qc-time&${searchParams.toString()}`;
                              window.open(url, "_blank");
                              break;
                            case "QC Pending":
                              url = `/report/data?title=${encodeURI(
                                "QC Pending"
                              )}&base_url=/v5/ops-analytics/qc&path=/data/print-qc-pending&${searchParams.toString()}`;
                              window.open(url, "_blank");
                              break;
                            case "QC In Progress":
                              url = `/report/data?title=${encodeURI(
                                "QC In Progress"
                              )}&base_url=/v5/ops-analytics/qc&path=/data/print-qc-in-progress&${searchParams.toString()}`;
                              window.open(url, "_blank");
                              break;
                            case "QC Completed":
                              url = `/report/data?title=${encodeURI(
                                "QC Completed"
                              )}&base_url=/v5/ops-analytics/qc&path=/data/print-qc-completed&${searchParams.toString()}`;
                              window.open(url, "_blank");
                              break;
                          }
                        }}
                      />
                    </Grid2>
                  )
                )}
              </Grid2>
            </Box>
          </Box>
        </>
      ) : (
        <PermissionBlockLabel />
      )}
    </>
  );
};

export default observer(QCAnalyticsDashboard);
